import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Target, LayoutDashboard, FileText, QrCode, GitBranch, Upload } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";
import Seo from "../../components/Seo";
import api from "../../lib/api";

const ICONS = { "Reliable Source Attribution": Target, "Needs Attention Dashboard": LayoutDashboard, "No-Code Form Builder": FileText, "Trackable QR Codes": QrCode, "Pipelines & Follow-Ups": GitBranch, "Safe Imports": Upload };

export default function ProductCRM() {
  const [product, setProduct] = useState(null);
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    api.get("/products/crm").then(({ data }) => setProduct(data)).catch(() => {});
    api.get("/plans").then(({ data }) => setPlans(data.items)).catch(() => {});
  }, []);

  return (
    <MarketingLayout>
      <Seo title={product?.seo?.title || "AllToMAIDIt CRM"} description={product?.seo?.description} />

      <div className="relative marketing-grid-bg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28">
          <p className="eyebrow text-alto-red mb-5">AllToMAIDIt CRM</p>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold leading-[0.95]">CAPTURE.<br />FOLLOW UP.<br /><span className="text-alto-red">GROW.</span></h1>
          <p className="text-neutral-300 mt-7 max-w-2xl text-sm sm:text-lg leading-relaxed">Capture every contact. Know where they came from. Know what happened next. Make sure somebody follows up.</p>
          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <Link to="/app/register" data-testid="crm-trial-cta" className="btn-red font-bold px-7 py-4 rounded-md inline-flex items-center justify-center gap-2 text-sm">START YOUR TRIAL <ArrowRight size={18} /></Link>
            <a href="#pricing" data-testid="crm-pricing-cta" className="font-bold px-7 py-4 rounded-md border border-white/20 hover:border-alto-red hover:text-alto-red transition-colors text-sm text-center">VIEW PRICING</a>
            <Link to="/app/login" className="font-bold px-7 py-4 rounded-md text-neutral-300 hover:text-white text-sm text-center">SIGN IN</Link>
          </div>
        </div>
      </div>

      {/* FEATURES */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-12">Everything you need to never drop a lead.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {(product?.features || []).map((f) => {
            const Ic = ICONS[f.title] || Check;
            return (
              <div key={f.title} className="card-hairline rounded-xl p-6 bg-alto-charcoal" data-testid={`crm-feature-${f.title.replace(/\s+/g,'-').toLowerCase()}`}>
                <Ic className="text-alto-red mb-5" size={26} />
                <h3 className="font-display text-lg font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-neutral-400 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* PRICING */}
      <div id="pricing" className="border-y border-white/10 bg-alto-panel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
          <p className="eyebrow text-alto-red mb-3 text-center">Pricing</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3 text-center">Simple, scalable plans.</h2>
          <p className="text-neutral-400 text-center max-w-xl mx-auto mb-14 text-sm">Start small and grow. Pricing and limits are fully configurable per organization.</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            {plans.map((p, idx) => (
              <div key={p.key} data-testid={`pricing-${p.key}`} className={`rounded-2xl p-8 border ${idx === 1 ? "border-alto-red bg-black" : "border-white/10 bg-black/50"}`}>
                <h3 className="font-display text-xl font-bold">{p.name}</h3>
                <p className="text-sm text-neutral-400 mt-1 min-h-[40px]">{p.tagline}</p>
                <div className="my-6">
                  <span className="font-display text-4xl font-extrabold">${p.price_monthly}</span>
                  <span className="text-neutral-500 text-sm">/mo</span>
                </div>
                <Link to="/app/register" className={`block text-center font-bold px-5 py-3 rounded-md text-sm mb-6 ${idx === 1 ? "btn-red" : "border border-white/20 hover:border-alto-red"}`}>START YOUR TRIAL</Link>
                <ul className="space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="text-alto-red shrink-0 mt-0.5" size={15} />{f}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-neutral-500 mt-8">Founding & nonprofit organizations may qualify for complimentary accounts. <Link to="/contact" className="text-alto-red">Contact us</Link>.</p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-alto-red/15 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Ready to capture every contact?</h2>
          <Link to="/app/register" className="btn-red font-bold px-8 py-4 rounded-md inline-flex items-center gap-2 mt-8 text-sm">START YOUR TRIAL <ArrowRight size={18} /></Link>
        </div>
      </div>
    </MarketingLayout>
  );
}
